import OpenAI from 'openai';

// Initialize OpenAI client (API key will be provided by user)
let openaiClient: OpenAI | null = null;

export const initializeOpenAI = (apiKey: string): boolean => {
  try {
    openaiClient = new OpenAI({
      apiKey: apiKey,
      dangerouslyAllowBrowser: true // Note: In production, use a backend proxy
    });
    return true;
  } catch (error) {
    console.error('Failed to initialize OpenAI:', error);
    return false;
  }
};

export const enhancePrompt = async (
  basePrompt: string,
  platform: string = 'midjourney'
): Promise<string> => {
  if (!openaiClient) throw new Error('OpenAI not initialized');

  const systemPrompt = `You are an expert AI prompt engineer. Enhance the given prompt for ${platform}. 
  Make it more detailed, creative, and optimized for best results. 
  Include relevant style parameters, lighting, composition, and quality modifiers.
  Keep the enhanced prompt concise but comprehensive.`;

  try {
    const completion = await openaiClient.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: `Enhance this prompt: ${basePrompt}` }
      ],
      temperature: 0.7,
      max_tokens: 200
    });

    return completion.choices[0].message.content || basePrompt;
  } catch (error) {
    console.error('Error enhancing prompt:', error);
    throw error;
  }
};

export const generateVariations = async (
  prompt: string,
  count: number = 3
): Promise<string[]> => {
  if (!openaiClient) throw new Error('OpenAI not initialized');

  try {
    const completion = await openaiClient.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { 
          role: "system", 
          content: `Generate ${count} creative variations of the given prompt. 
          Each should maintain the core concept but explore different styles, moods, or perspectives.
          Return only the prompts, one per line.`
        },
        { role: "user", content: prompt }
      ],
      temperature: 0.9,
      max_tokens: 400
    });

    const variations = completion.choices[0].message.content
      ?.split('\n')
      .filter(line => line.trim())
      .map(line => line.replace(/^\d+\.\s*/, '')) || [];

    return variations.slice(0, count);
  } catch (error) {
    console.error('Error generating variations:', error);
    throw error;
  }
};

export const convertPromptFormat = async (
  prompt: string,
  fromPlatform: string,
  toPlatform: string
): Promise<string> => {
  if (!openaiClient) throw new Error('OpenAI not initialized');

  try {
    const completion = await openaiClient.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { 
          role: "system", 
          content: `Convert prompts between different AI platforms. 
          From ${fromPlatform} format to ${toPlatform} format.
          Adapt parameters, syntax, and style modifiers appropriately.`
        },
        { role: "user", content: `Convert this ${fromPlatform} prompt to ${toPlatform}: ${prompt}` }
      ],
      temperature: 0.3,
      max_tokens: 200
    });

    return completion.choices[0].message.content || prompt;
  } catch (error) {
    console.error('Error converting prompt:', error);
    throw error;
  }
};

export const suggestImprovements = async (prompt: string): Promise<string[]> => {
  if (!openaiClient) throw new Error('OpenAI not initialized');

  try {
    const completion = await openaiClient.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { 
          role: "system", 
          content: `Analyze the prompt and suggest specific improvements.
          Provide 3-5 actionable suggestions to make it better.
          Focus on clarity, detail, and AI model compatibility.`
        },
        { role: "user", content: `Suggest improvements for: ${prompt}` }
      ],
      temperature: 0.5,
      max_tokens: 300
    });

    const suggestions = completion.choices[0].message.content
      ?.split('\n')
      .filter(line => line.trim())
      .map(line => line.replace(/^[-•*]\s*/, '')) || [];

    return suggestions;
  } catch (error) {
    console.error('Error suggesting improvements:', error);
    throw error;
  }
};

export const isOpenAIInitialized = (): boolean => {
  return openaiClient !== null;
};